<script>
/**
 * FuIcon — the Follow-ups redesign's icon set, as inline SVG (redesign Phase 1A, 2026-10-02).
 *
 *   <fu-icon name="cal" :size="16" :stroke="1.9" />
 *
 * The shapes are the Claude Design export's ICONS map (Follow-ups.dc.html), which is Lucide path
 * data — Lucide is ISC/MIT licensed (https://lucide.dev/license). No icon font and no new
 * dependency: each icon is a list of [tag, attrs] drawn on a 24×24 viewBox, stroked in
 * currentColor, so it takes the colour of the text around it.
 *
 * Functional (no instance) because a 50-row table draws several per row. It has no <style> of its
 * own: the <svg> inherits the PARENT's scoped-style attribute, so a consumer styles `.fu-icon`
 * (and the spinner's rotation) from its own scoped block. Decorative by default (aria-hidden);
 * pass `label` when the icon is the only thing that says what a control does.
 */

const C = (cx, cy, r) => ['circle', { cx, cy, r }];
const P = d => ['path', { d }];
const R = (x, y, width, height, rx) => ['rect', { x, y, width, height, rx }];

const ICONS = {
  search: [C(11, 11, 7), P('m21 21-4.2-4.2')],
  cal: [R(3, 4.5, 18, 17, 2.5), P('M16 2.5v4M8 2.5v4M3 9.5h18')],
  calx: [R(3, 4.5, 18, 17, 2.5), P('M16 2.5v4M8 2.5v4M3 9.5h18M10 13l4 4M14 13l-4 4')],
  chev: [P('m6 9 6 6 6-6')],
  chevL: [P('m15 6-6 6 6 6')],
  chevR: [P('m9 6 6 6-6 6')],
  up: [P('m18 15-6-6-6 6')],
  down: [P('m6 9 6 6 6-6')],
  sort: [P('m7 15 5 5 5-5M7 9l5-5 5 5')],
  check: [P('M20 6 9 17l-5-5')],
  x: [P('M18 6 6 18M6 6l12 12')],
  plus: [P('M12 5v14M5 12h14')],
  phone: [P('M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8 9.8a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2z')],
  mail: [R(2.5, 4.5, 19, 15, 2.5), P('m3 7 9 6 9-6')],
  text: [P('M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z')],
  ban: [C(12, 12, 9), P('m5.6 5.6 12.8 12.8')],
  restart: [P('M3 12a9 9 0 1 0 3-6.7L3 8'), P('M3 3v5h5')],
  alert: [C(12, 12, 9), P('M12 8v4M12 16h.01')],
  clock: [C(12, 12, 9), P('M12 7v5l3.5 2')],
  note: [P('M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z'), P('M14 3v6h6M8 13h8M8 17h5')],
  list: [P('M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01')],
  users: [P('M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2'), C(9, 7, 4), P('M22 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8')],
  sliders: [P('M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6')],
  trash: [P('M3 6h18M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6M10 11v6M14 11v6M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2')],
  chart: [P('M3 3v18h18'), P('M7 16v-4M12 16V8M17 16v-7')],
  lock: [R(4, 11, 16, 10, 2), P('M8 11V7a4 4 0 0 1 8 0v4')],
  bot: [R(4, 8, 16, 12, 3), P('M12 8V4M9 14h.01M15 14h.01')],
  info: [C(12, 12, 9), P('M12 11v5M12 8h.01')],
  history: [P('M3 12a9 9 0 1 0 9-9 9.7 9.7 0 0 0-6.7 2.7L3 8'), P('M3 3v5h5M12 7v5l4 2')],
  arrowR: [P('M5 12h14M13 6l6 6-6 6')],
  recall: [C(12, 12, 9), P('M12 7v5l3 2')],
  receipt: [P('M4 2v20l3-2 3 2 3-2 3 2 3-2 1 .7V2l-1 .7-3-2-3 2-3-2-3 2-3-2z'), P('M8 8h8M8 12h8M8 16h5')],
  dollar: [P('M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6')],
  ext: [P('M15 3h6v6M10 14 21 3M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6')],
  bell: [P('M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9'), P('M10.3 21a1.94 1.94 0 0 0 3.4 0')],
  pin: [P('M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z'), C(12, 10, 3)],
  home: [P('m3 9.5 9-7 9 7V20a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z'), P('M9 22V12h6v10')],
  mobile: [R(6, 2, 12, 20, 2.5), P('M11 18h2')],
  brief: [R(2.5, 7, 19, 13, 2), P('M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2')],
  edit: [P('M12 20h9'), P('M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z')],
  send: [P('m22 2-7 20-4-9-9-4Z'), P('M22 2 11 13')],
  menu: [P('M4 6h16M4 12h16M4 18h16')],
  spin: [P('M21 12a9 9 0 1 1-6.2-8.6')],
};

export default {
  name: 'FuIcon',
  functional: true,
  props: {
    name: { type: String, required: true },
    size: { type: [Number, String], default: 16 },
    stroke: { type: [Number, String], default: 1.9 },
    // An accessible name, only when nothing else on the control says what it does.
    label: { type: String, default: '' },
  },
  render(h, { props, data }) {
    const parts = ICONS[props.name] || [];
    const a11y = props.label
      ? { role: 'img', 'aria-label': props.label }
      : { 'aria-hidden': 'true' };
    return h('svg', {
      staticClass: ['fu-icon', data.staticClass].filter(Boolean).join(' '),
      class: data.class,
      style: data.style,
      attrs: Object.assign({
        width: props.size,
        height: props.size,
        viewBox: '0 0 24 24',
        fill: 'none',
        stroke: 'currentColor',
        'stroke-width': props.stroke,
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        focusable: 'false',
        'data-icon': props.name,
      }, a11y, data.attrs),
    }, parts.map(([tag, attrs]) => h(tag, { attrs })));
  },
};
</script>
